import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthTenant } from '@Core/enums/auth-tenant.enum';
import { UnifiedAuthContext } from '@Core/interfaces/auth-config.interface';
import { AuthTenantContextService } from '@Core/services/auth-tenant-context.service';
import { AuthService } from '@Core/services/auth.service';

@Component({
    standalone: false,
    selector: 'login-container',
    templateUrl: './login-container.component.html',
})
export class LoginContainerComponent implements OnInit {

    private destroyRef = inject(DestroyRef);

    /** True when serving the secondary (alt) login route. */
    isAltLogin = false;

    selectTenants = false;
    selectedTenant: string;
    tenants: { key: string; name: string }[] = [];

    recover_password_invalid: boolean;
    public errorMessage: string;
    public isAuthenticated: boolean;
    loading: boolean;

    private tenant: AuthTenant;

    constructor(
        private unifiedAuth: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private authTenantContext: AuthTenantContextService
    ) { }

    ngOnInit(): void {
        // Determine tenant from route data (defaults to Primary)
        this.tenant = this.route.snapshot.data['tenant'] || AuthTenant.Primary;
        this.isAltLogin = this.tenant === AuthTenant.Secondary;

        this.authTenantContext.setSelectedTenant(this.tenant);

        const hasCodeParam = window.location.search.includes('code=');
        const hasStateParam = window.location.search.includes('state=');
        const hasHashCode = window.location.hash.includes('code=');
        const hasHashState = window.location.hash.includes('state=');

        this.recover_password_invalid = false;
        this.loading = false;
        this.isAuthenticated = false;

        // Subscribe to auth context changes
        this.unifiedAuth.authContext$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((authContext) => {
            if (authContext) {
                this.isAuthenticated = true;
                this.checkAuthContextAndRedirect(authContext);
            } else {
                this.isAuthenticated = false;
                this.selectTenants = false;
                this.tenants = [];
                this.selectedTenant = null;
            }
        });

        const shouldHandleCallback = hasCodeParam || hasStateParam
            || hasHashCode || hasHashState;

        if (shouldHandleCallback) {
            // Check if this is a secondary auth callback that landed on the wrong page
            if (!this.isAltLogin
                && this.authTenantContext.getSelectedTenant() === AuthTenant.Secondary) {
                const currentParams = window.location.search;
                this.router.navigateByUrl('/altlogin' + currentParams);
                return;
            }
            this.handleAuthCallback();
        }
    }

    private async handleAuthCallback(): Promise<void> {
        this.loading = true;
        try {
            await this.unifiedAuth.handleRedirectCallback();
        } catch (error) {
            this.errorMessage = 'Authentication callback failed: '
                + (error.message || error);
            this.loading = false;
        }
    }

    private checkAuthContextAndRedirect(authContext: UnifiedAuthContext): void {
        this.tenants = this.unifiedAuth.tenants || [];

        if (!this.tenants.length && authContext?.tenants) {
            const tenantKeys: string[] = authContext.tenants || [];
            this.tenants = tenantKeys.map(key => ({ key, name: key }));
        }

        // When there are multiple tenants, show the tenant selector and
        // clear the loading overlay.  When there are zero tenants for a
        // non-admin, show the selector so the empty-state message is
        // displayed.  For admins and single-tenant users the service
        // handles navigation, so keep the overlay visible.
        this.selectTenants = this.tenants.length !== 1
            && !this.unifiedAuth.isAdmin;
        if (this.selectTenants) {
            this.loading = false;
        }
    }

    changeUser(): void {
        this.authTenantContext.clearSelectedTenant();
        this.unifiedAuth.logout(true);
    }

    tenantSelect(tenant: { key: string; name: string }): void {
        this.loading = true;
        this.unifiedAuth.tenantSelect(tenant).subscribe({
            error: () => {
                this.loading = false;
            }
        });
    }

    async onLogin(): Promise<void> {
        this.loading = true;
        try {
            await this.unifiedAuth.loginWithTenant(this.tenant);
        } catch (error) {
            this.errorMessage = 'Login failed';
            this.loading = false;
        }
    }

    goToPrimaryLogin(): void {
        this.router.navigate(['']);
    }
}

